import * as angular from 'angular';

import * as buyers from './../buyers.service';
import * as categories from './../categories.service';
import * as sellers from './../sellers.service';

import RfpBuyersService from './rfpBuyers.service';
import RfpMixesService from './rfpMixes.service';
import RfpOffersService from './rfpOffers.service';
import RfpSellersService from './rfpSellers.service';
import RfpUnspscsService from './rfpUnspscs.service';
import { Offer } from './../offers.service';

interface Benchmark {
  plannedSpend: {
    similarBuyers: number;
    allBuyers: number;
  };
  continuedSpend: {
    similarBuyers: number;
    allBuyers: number;
  };
}

export interface Mix {
  id: number;
  incumbentAllocatedMarketshare: number;
  incumbentPlannedSpend: number;
  name: string;
  continuedSpend: number;
  plannedSpend: number;
  offers: MixOffer[];
  benchmark: Benchmark;
  description: string;
  mixType: string;
  optimizedForOfferId: any;
}

export interface MixOffer {
  id: number;
  seller: NameId;
  selected: boolean;
  contracted: boolean;
  selectionOrder: number;
  selectedTerm: {
    duration: number;
    targetMarketShare: number;
    pricingType: string;
  };
  supportedTerms: {
    pricingType: string;
    durations: number[];
  }[];
  allocatedMarketShare: number;
  plannedSpendInSellerUnspscs: number;
  plannedSpend: number;
}

export interface Offers {
  id: number;
  submissionDate: string;
  status: string;
  // contractActivationMinLeadTime: number;
  state: {
    reasonsPreventingSubmission: string[];
    canRetractOffer: boolean;
    pricingIsComplete: boolean;
    canSubmitOffer: boolean;
    retractionPeriodEndDateTime: string;
  };
  pricingModel: {
    id: number;
  };
  productPricingStatus: string;
  spendVolume: number;
  seller: {
    id: number;
    logoUrl: string;
    name: string;
    termsUrl: string;
  };
  secondsToRespond: number;
  commitmentType?: string;
}

export interface ParticipantMember {
  categorySpend: number;
  included: boolean;
  prevCatPriority: any;
  profile: ProfileSummary;
  requestParticipantId: number;
  version: number;
}

interface CategoryUnspscSummary {
  displayName: string;
  id: number;
  isCarveIn: boolean;
  label: string; // ?
  name: string;
  smartKey: string; // ?
  carveInProductCount: number;
}

interface CategoryUnspsc extends CategoryUnspscSummary {
  standardCategory: {
    id: number;
    name: string;
    type: string;
  };
  standardCategoryId: number;
  standardCategoryName: string;
}

export interface PotentialCategory {
  buyerEfficiency: number;
  buyerEfficiencyStar: number;
  id: number;
  name: string;
  potentialSavings: number;
  type: string;
  unspscs: CategoryUnspscSummary[];
  isCarveIn: boolean;
}

export interface PotentialCategoryResponse {
  categories: PotentialCategory[];
}

export interface SpendDetail {
  actualSpend: number;
  categoryId: number;
  continuedSpend: number;
  sellerId: number;
  unitsPurchased: number;
  unspscId: number;
  unspscName: string;
  isCarveIn: boolean;
}

// Why is this different than CategoryUnspscSummary? Can backend make them the same?
export interface SellerUnspscSummary {
  category: {
    agreementId: number;
    id: number;
    name: string;
    type: any; // ?
  };
  displayName: string;
  id: number;
  isCarveIn: boolean;
  itemsOffered: number;
  label: string; // ?
  name: string;
  smartKey: string; // ?
}

export interface PotentialSeller {
  category: number; // ??
  id: number;
  logoUrl: string;
  name: string;
  unspscs: SellerUnspscSummary[];
}

export interface RequestedBy {
  email: string;
  firstName: string;
  id: number;
  lastName: string;
  name: string;
  userId: number;
}

export interface SubmittedBy {
  emailAddress: string;
  firstName: string;
  id: number;
  lastName: string;
}

interface SystemAddress {
  address1: string;
  address2: string;
  addressType: string;
  city: string;
  id: number;
  state: string;
  zip: string;
}

interface AmplifyRfpSystem {
  actualSpend: number;
  address: SystemAddress;
  contactPhone;
  continuedSpend: number;
  existingOffers;
  gln;
  id: number;
  memberId: string;
  name: string;
  organizationType?: string;
  parents;
  participant;
  potentialSavings;
}

interface AmplifyCategory {
  id: number;
  name: string;
  standardCategoryCoveragePct: number;
  type: string;
  unspscs: CategoryUnspsc[];
}

export interface AmplifyRfp {
  buyer: {
    categorySpend: buyers.CategorySpend;
    profile: buyers.Profile;
  };
  category: AmplifyCategory;
  categoryType: string;
  contractDurations: number[];
  contractEffectiveDates: number[];
  expirationDate: number;
  groupRfp: boolean;
  id: number;
  numberOfCpas: number;
  numberOfOffers: number;
  numberOfParticipants: number;
  numberOfSellers: number;
  numberOfStandardCategories: number;
  numberOfSystems: number;
  numberOfUnspscs: number;
  offers;
  offersDueDate;
  offersReceivedDate;
  preferredContractDuration: number;
  preferredContractEffectiveDate: number;
  sellers;
  status: string;
  submissionDate;
  submittedBy: {
    emailAddress: string;
    firstName: string;
    id: number;
    lastName: string;
  };
  systems: AmplifyRfpSystem[];
  timeline: {
    secondsLeft;
    secondsTotal;
    status: string;
  };
  type: string;
}

// TODO: Use the standard Rfp type above, putting this here temporarily because used by multiple modules
// Most things should be using RfpInfo instead of this, finish removing other references
// IMPROVE: (Update, Oct 2019) We should be using RfpVersionTwo globally, this was part of the VersionOne code
export interface Rfp_Mod {
  buyer: {
    categorySpend: buyers.CategorySpend;
    profile: {
      id: number;
      zip: string;
      gln: string;
      name: string;
      state: string;
      address1: string;
      address2: string;
      city: string;
    };
  };
  categoryId: number;
  categoryName: string;
  categorySpend: buyers.CategorySpend;
  categoryType: categories.categoryTypes;
  groupRequest: boolean;
  last12monthSpend: number;
  last12MonthsSpend: number;
  planId: number;
  pricingPreferences: {
    duration: number;
    effectiveDate: string;
    preferredDistributor: string;
    preferredDistributorId: number;
    possibleEffectiveDates: string[];
  };
  requestedBy: RequestedBy;
  rfpId: number;
  rfpType?: string;
  sellers: number[];
  sendDate: string;
  suppliersSelected: any;
  status: string;
  whenSent: string;
  isSIO: boolean;
  productCategory: {
    id: number;
    name: string;
  };
  requestId: number;
  offer: any;
}

export interface RfpVersionTwo {
  status: string;
  // message: string;
  // code: any;
  id: number;
  hasFormularyPrice: boolean;
  hasPartialCategories: boolean;
  carvedInUnspscs: [
    {
      id: number;
      isCarveIn: boolean;
      name: string;
      smartKey: any;
      displayName: string;
      label: string;
      unspscVersion: any;
      standardCategoryId: number;
      standardCategoryName: string;
      standardCategory: {
        id: number;
        name: string;
        type: string;
      };
    },
  ];
  category: {
    id: number;
    name: string;
    type: string;
    unspscs: [
      {
        id: number;
        isCarveIn: boolean;
        name: string;
        smartKey: any;
        displayName: string;
        label: string;
        unspscVersion: any;
        standardCategoryId: number;
        standardCategoryName: string;
        standardCategory: {
          id: number;
          name: string;
          type: string;
        };
      },
    ];
    standardCategoryCoveragePct: string;
  };
  groupRfp: boolean;
  submissionDate: number;
  offersReceivedDate: number;
  expirationDate: number;
  offersDueDate: number;
  preferredContractEffectiveDate: number;
  preferredContractDuration: number;
  contractDurations: any[];
  contractEffectiveDates: any[];
  type: string;
  submittedBy: {
    id: number;
    firstName: string;
    lastName: string;
    emailAddress: string;
  };
  buyer: NameId;
  numberOfSellers: number;
  numberOfOffers: number;
  // numberOfResponses: number;
  numberOfCpas: number;
  numberOfStandardCategories: number;
  numberOfUnspscs: number;
  numberOfSystems: number;
  numberOfParticipants: number;
  categoryType: string;
  purchasePlanAccessible: boolean;
  responseStatus: string;
  // releaseHoldDate: any;
  // mbrRecruitingCpa: string;
  timeline: {
    status: string;
    secondsLeft: number;
    secondsTotal: number;
    alertLevel: string;
  };
  offers: Offers[];
  sellers: any;
  rfpCustomSpendDateRange: {
    endDate: number;
    hasCustomSpendDateRange: boolean;
    startDate: number;
  };
  categorizationSchemeName: string;
}

// We should only ever be updating these fields
export interface Rfp_Update {
  duration: number;
  effectiveDate: string;
  updateContext: string;
}

export interface Seller {
  id: number;
  name: string;
  logoUrl: string;
  responseStatus: string;
  spend: any;
  termsUrl: string;
}

export interface SpendByMonth {
  categoryPotentialSpend: number;
  month: number;
  buyer: buyers.Profile;
  year: number;
  totalSpend: number;
  spendWithSeller: number;
  seller: sellers.Profile;
}

export interface SpendByUnspsc {
  categoryPotentialSpend: number;
  sellerMarketShare: number;
  name: string;
  buyer: buyers.Profile;
  totalSpend: number;
  spendWithSeller: number;
  seller: sellers.Profile;
}

export interface System {
  actualSpend: number;
  address: any;
  contactPhone: any;
  continuedSpend: number;
  existingOffers: any;
  gln: any;
  id: number;
  memberId: string;
  name: string;
  organizationType: string;
  parents: SystemParent[];
  participant: boolean;
  potentialSavings: number;
}

export interface SystemParent {
  id: number;
  level: number;
  name: string;
}

export interface ActivePurchasePlan {
  id: number;
  name: string;
  continuedSpend: number;
  plannedSpend: number;
  incumbentAllocatedMarketShare?: number;
  incumbentPlannedSpend: number;
  offers: MixOffer[];
  benchmark: Benchmark;
  basedOn: BasedOn;
  lastModifiedDate: number;
  lastModifiedBy: string;
  publishedBy: string;
  groupPlanPublished: boolean;
  groupPlanPublishEnabled: boolean;
  lastPlanPublicationDate: number;
  purchasePlanningMode: any;
  changedSinceLastPublished: boolean;
  planChanged: boolean;
  spendRecalculated: boolean;
  status?: string;
}

export interface PurchasePlans {
  purchasePlans: PurchasePlan[];
}

export interface PurchasePlan {
  incumbentPlannedSpend: number;
  offers: MixOffer[];
  publishedBy: string;
  lastModifiedDate: string;
  changedSinceLastPublished: boolean;
  lastModifiedBy: string;
  plannedSpend: number;
  groupPlanPublishEnabled: boolean;
  lastPlanPublicationDate: string;
  benchmark: Benchmark;
  planContracted: boolean;
  lockedAllocationCount: number;
  groupPlanPublished: boolean;
  incumbentAllocatedMarketShare: number;
  planChanged: boolean;
  name: string;
  continuedSpend: number;
  cachedXrefApplied: boolean;
  purchasePlanningMode: any;
  id: number;
  ownedBy: NameId;
  basedOn: BasedOn;
  spendRecalculated: boolean;
  status?: string;
}

interface BasedOn {
  name: string;
  purchasePlanId: number;
  optimizedForOfferId: any;
  source: string;
  mode?: any;
  statusDate?: string;
  partnerCommitmentLevel?: number;
  commitmentLevel?: number;
  contracted?: boolean;
  status?: any;
}

interface MixSummaries {
  mixes: MixSummary[];
}

export interface MixSummary {
  name: string;
  description: string;
  mixType: string;
  mixQueryParam: {
    offers: string;
    selectedTerm: any;
  };
}

export interface PricingModelOptions {
  pricingModelId: number;
  pricingType?: string;
  duration?: number;
  spendVolume?: number;
}

export interface SpendWithSeller {
  next12monthSpend: number;
  acttualSpend: number;
  categoryPotentialSpend: number;
  spendWithSeller: number;
  crossReferenceSpend: number;
  last12monthSpend: number;
  continuedSpend: number;
  categorySavings: number;
}

interface NameId {
  organizationType: string;
  id: number;
  name: string;
}

interface ProfileSummary {
  id: number;
  name: string;
}

interface StandardCategorySummary {
  id: number;
  name: string;
  type: string;
}

interface UnspscSummary {
  id: number;
  displayName: string;
  label: any;
  name: string;
  smartKey: any;
  standardCategory: StandardCategorySummary;
  standardCategoryId: number;
  standardCategoryName: string;
  unspscVersion: any;
}

export interface CategorySummary {
  id: number;
  name: string;
  standardCategoryCoveragePct: any;
  type: string;
  unspscs: UnspscSummary[];
}

interface SellerSummary {
  id: number;
  logoUrl: string;
  name: string;
}

export interface OfferSummary {
  cpa: any;
  id: number;
  seller: SellerSummary[];
  status: string;
  submissionDate: number;
}

interface SystemSummary {
  actualSpend: any;
  address: {};
  contactPhone: any;
  continuedSpend: any;
  existingOffers: any;
  gln: any;
  id: number;
  memberId: string; // number as string
  name: string;
  organizationType: string; // Enum?
  parents: any;
  participant: any;
  potentialSavings: any;
}

export interface RfpSubmittedBy {
  emailAddress: string;
  firstName: string;
  id: number;
  lastName: string;
}

/**
 * Used by:
 * - getRfps()
 */
export interface Rfp {
  buyer: ProfileSummary;
  category: CategorySummary;
  categoryType: string; // TODO: ENUM. STANDARD_CATEGORY or ??
  contractDurations: number[];
  contractEffectiveDates: number[];
  expirationDate: number;
  groupRfp: boolean;
  id: number;
  numberOfCpas: number;
  numberOfOffers: number;
  numberOfParticipants: number;
  numberOfSellers: number;
  numberOfStandardCategories: number;
  numberOfSystems: number;
  numberOfUnspscs: number;
  offers: OfferSummary[];
  offersDueDate: number;
  offersReceivedDate: number;
  preferredContractDuration: number;
  preferredContractEffectiveDate: number;
  purchasePlanAccessible: boolean;
  responseStatus:
    | 'OfferSubmitted'
    | 'OfferExpired'
    | 'OfferRejected'
    | 'OfferAccepted'
    | 'OfferWorkInProgress';
  sellers: SellerSummary[];
  status: string; // TODO: ENUM
  submissionDate: number;
  submittedBy: RfpSubmittedBy;
  systems: SystemSummary[];
  timeline: {
    alertLevel: any;
    secondsLeft: number;
    secondsTotal: number;
    status: string; // TODO: Enum
  };
  type: string; // TODO: Enum
}

/**
 * Used by:
 * - getRfpVersionOne(rfpId)
 */
export interface RfpVersionOne {
  buyer: {
    categorySpend: any;
    profile: any;
  };
  categoryType: string;
  contractIds: number[];
  duration: number;
  effectiveDate: number;
  includedParticipantCount: number;
  isGroupRequest: boolean;
  offer: any;
  optOutPeriodEndDate: number;
  participants: {
    includedCount: number;
    members: {
      categorySpend: number;
      included: boolean;
      prevCatPriority: any;
      profile: any;
      requestParticipantId: number;
      version: number;
    }[];
    totalCount: number;
  };
  possibleEffectiveDates: number[];
  preferredDistributor: {
    distributorId: number;
    name: string;
  };
  pricingExpirationDate: number;
  productCategory: {
    id: number;
    name: string;
    type: string;
  };
  purchasePlanId: number;
  requestId: number;
  requestedBy: RequestedBy;
  responseDueDate: number;
  rfpType: string;
  sellers: any[];
  sendDate: number;
  status: string;
}

export interface OfferImpacts {
  plannedSpend: number;
  continuedSpend: number;
  threshold: number;
  savings: number;
  numberOfConversions: number;
  facilities: Facility[];
  minAllocationPercentage: number;
}

interface Facility {
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  isSeller: boolean;
  categorySpend: {
    next12monthSpend: number;
    categoryPotentialSpend: number;
    acttualSpend: number; // typo in api, use actualSpend below
    spendWithSeller: number;
    crossReferenceSpend: number;
    actualSpend: number;
    last12monthSpend: number;
    continuedSpend: number;
    categorySavings: number;
  };
  gln: string;
  hasAptitudeAgreement: boolean;
  isBuyer: boolean;
  logoUrl: string;
  organizationType: string;
  isSystem: boolean;
  children: Facility[];
  includedInRfp: boolean;
  name: string;
  id: number;
  savings: number;
  isGroup: boolean;
  existingOffers: string;
  parents: {
    level: number;
    name: string;
    id: number;
  }[];
}

export default class RfpService {
  baseUrl = '/aptitude/api/rfps';

  mixes: RfpMixesService;
  offers: RfpOffersService;
  buyers: RfpBuyersService;
  sellers: RfpSellersService;
  unspscs: RfpUnspscsService;

  constructor(private $http) {
    'ngInject';

    this.mixes = new RfpMixesService($http);
    this.offers = new RfpOffersService($http);
    this.buyers = new RfpBuyersService($http);
    this.sellers = new RfpSellersService($http);
    this.unspscs = new RfpUnspscsService($http);
  }

  getRfps(type: string, view: string, cache = true): ng.IPromise<Rfp[]> {
    return this.$http.get(`${this.baseUrl}`, {
      params: {
        type,
        view,
      },
      cache,
    });
  }

  /**
   * This is the current API all of our modern pages should be using to get rfp details
   *
   * IMPROVE: Can we get rid of Rfp? Then update RfpVersionTwo to Rfp
   */
  getRfp(rfpId: number, cache = false): ng.IPromise<RfpVersionTwo> {
    return this.$http.get(`${this.baseUrl}/${rfpId}?version=2.0`, { cache });
  }

  /**
   * Deprecated but still used by old purchase planning / RFP pages
   * Any code still calling this needs to be updated to call getRfp() above
   *
   * @param {number} rfpId
   */
  getRfpVersionOne(rfpId: number): ng.IPromise<RfpVersionOne> {
    console.warn(
      'The getRfpVersionOne API has been deprecated. Please update this code.',
    );

    return this.$http.get(`${this.baseUrl}/${rfpId}`);
  }

  getProducts(rfpId: number): ng.IPromise<any> {
    return this.$http.get(`${this.baseUrl}/${rfpId}/products`);
  }

  createRFPForApex(
    buyerId?: number,
    agreementIds?: any[],
    unspscIds?: any[],
    spendSourceId?: string,
    spendSource?: string,
    status?: string,
  ) {
    return this.$http.post(this.baseUrl, {
      buyerId: buyerId,
      agreementIds: agreementIds,
      unspscIds: unspscIds,
      spendSourceId: spendSourceId,
      spendSource: spendSource,
      status: status,
    });
  }

  createRFPwithTiers(
    buyerId?: number,
    categoryId?: number,
    sellerId?: number,
    tiers?: any[],
  ) {
    return this.$http.post(this.baseUrl, {
      buyer: buyerId,
      productCategory: categoryId,
      seller: sellerId,
      tiers: tiers,
    });
  }

  // send rfp to seller
  sendRfpToSeller(rfpId: number): ng.IPromise<any> {
    return this.$http.put(`${this.baseUrl}/${rfpId}/status`, {
      status: 'SEND',
    });
  }

  getPotentialSellers(rfpId: number): ng.IPromise<SellerUnspscSummary> {
    return this.$http.get(`${this.baseUrl}/${rfpId}/potentialsellers`);
  }

  getPotentialCategories(
    rfpId: number,
  ): ng.IPromise<PotentialCategoryResponse> {
    return this.$http.get(`${this.baseUrl}/${rfpId}/potentialcategories`);
  }

  getSpendDetail(rfpId: number): ng.IPromise<SpendDetail[]> {
    return this.$http.get(`${this.baseUrl}/${rfpId}/spenddetail`);
  }

  getSystems(rfpId: number): ng.IPromise<System[]> {
    return this.$http.get(`${this.baseUrl}/${rfpId}/systems`);
  }

  // Called with OM-1 flag off
  getOfferImpacts(
    rfpId: number,
    partnerId: number,
    offerId: number,
    tierId: number,
  ): ng.IPromise<OfferImpacts> {
    return this.$http.get(
      `${this.baseUrl}/${rfpId}/systems/${partnerId}/offerimpacts?offerId=${offerId}&tierId=${tierId}`,
    );
  }

  getOfferImpactsForSeller(
    rfpId: number,
    partnerId: number,
    offerId: number,
    tierId: number,
    usePrimaryCrossReferences: boolean,
  ): ng.IPromise<OfferImpacts> {
    return this.$http.get(
      `${this.baseUrl}/${rfpId}/systems/${partnerId}/offerimpacts?offerId=${offerId}&tierId=${tierId}&usePrimaryCrossReferences=${usePrimaryCrossReferences}`,
    );
  }

  getOfferImpactsForBuyer(
    rfpId: number,
    partnerId: number,
    offerId: number,
    masterPlanId: number,
  ): ng.IPromise<OfferImpacts> {
    return this.$http.get(
      `${this.baseUrl}/${rfpId}/systems/${partnerId}/offerimpacts?offerId=${offerId}&purchasePlanId=${masterPlanId}`,
    );
  }

  updateParticipants(rfpId: number, systems: any): ng.IPromise<any> {
    return this.$http.put(`${this.baseUrl}/${rfpId}/participants`, systems);
  }

  renameCategory(
    rfpId: number,
    categoryName: string,
    suppressAction = false,
  ): ng.IPromise<any> {
    return this.$http.put(
      `${this.baseUrl}/${rfpId}/category`,
      {
        categoryName,
      },
      {
        suppressAction,
      },
    );
  }

  updateRfp(rfpId: number, rfpData): ng.IPromise<any> {
    return this.$http.put(`${this.baseUrl}/${rfpId}`, rfpData);
  }

  updatePricingModel(rfpId: number, offerId: number, pricingModelId: number) {
    return this.$http.put(`${this.baseUrl}/${rfpId}/offers/${offerId}`, {
      pricingModel: {
        id: pricingModelId,
      },
    });
  }

  updateVolume(rfpId: number, offerId: number, spendVolume: number) {
    return this.$http.put(`${this.baseUrl}/${rfpId}/offers/${offerId}`, {
      spendVolume: Math.floor(spendVolume),
    });
  }

  updatePricingModelAndVolume(
    rfpId: number,
    offerId: number,
    pricingModelId: number,
    spendVolume: number,
  ) {
    return this.$http.put(`${this.baseUrl}/${rfpId}/offers/${offerId}`, {
      pricingModel: {
        id: pricingModelId,
      },
      spendVolume: Math.floor(spendVolume),
    });
  }

  updateMinLeadTime(rfpId: number, offerId: number, minLeadTime: number) {
    return this.$http.put(`${this.baseUrl}/${rfpId}/offers/${offerId}`, {
      contractActivationMinLeadTime: minLeadTime,
    });
  }

  getUnspscs(rfpId: number) {
    return this.$http.get(`${this.baseUrl}/${rfpId}/unspscs`, {
      cache: true,
    });
  }

  getSpendByUnspsc(rfpId: number, { cache = false } = {}) {
    return this.$http.get(`${this.baseUrl}/${rfpId}/spendByUnspsc`, { cache });
  }

  getSpendByMonth(rfpId: number): ng.IPromise<SpendByMonth[]> {
    return this.$http.get(`${this.baseUrl}/${rfpId}/spendByMonth`, {
      cache: true,
    });
  }

  getActivePurchasePlan(
    rfpId: number,
    memberId?: number,
  ): ng.IPromise<ActivePurchasePlan> {
    return this.$http.get(`${this.baseUrl}/${rfpId}/purchaseplan`, {
      params: {
        memberId,
      },
    });
  }

  getSavedPurchasePlans(rfpId: number): ng.IPromise<PurchasePlans> {
    return this.$http.get(`${this.baseUrl}/${rfpId}/purchaseplans`);
  }

  getMixes(rfpId: number): ng.IPromise<any> {
    return this.$http.get(`${this.baseUrl}/${rfpId}/mixes`);
  }

  submitPublishPlanOptions(
    rfpId: number,
    purchasePlanningMode,
    purchasePlanIds,
    optInAllParticipants,
  ): ng.IPromise<any> {
    // TODO: need a better name for this method
    return this.$http.post(`${this.baseUrl}/${rfpId}/publications`, {
      purchasePlanIds: purchasePlanIds || [],
      purchasePlanningMode,
      optInAllParticipants,
    });
  }

  // We'll get a 204 No Content response if this succeeds
  loadSavedPlan(rfpId: number, savedPlanId) {
    return this.$http.put(`${this.baseUrl}/${rfpId}/purchaseplan`, {
      source: 'SAVED_PLAN',
      purchasePlanId: savedPlanId,
    });
  }

  // We'll get a 204 No Content response if this succeeds
  loadCurrentMix(
    rfpId: number,
    usePrimaryCrossReferences: boolean,
    useLockedAllocations: boolean,
  ): ng.IPromise<any> {
    return this.$http.put(`${this.baseUrl}/${rfpId}/purchaseplan`, {
      source: 'CURRENT_MIX',
      usePrimaryCrossReferences,
      useLockedAllocations,
    });
  }

  // We'll get a 204 No Content response if this succeeds
  loadBestMix(
    rfpId: number,
    usePrimaryCrossReferences: boolean,
    useLockedAllocations,
  ): ng.IPromise<any> {
    return this.$http.put(`${this.baseUrl}/${rfpId}/purchaseplan`, {
      source: 'BEST_MIX',
      usePrimaryCrossReferences,
      useLockedAllocations,
    });
  }

  // We'll get a 204 No Content response if this succeeds
  loadOptimizedMix(
    rfpId: number,
    purchasePlanId: number,
    optimizedForOfferId,
    selectedTerm,
    usePrimaryCrossReferences: boolean,
    useLockedAllocations: boolean,
  ): ng.IPromise<any> {
    return this.$http.put(`${this.baseUrl}/${rfpId}/purchaseplan`, {
      source: 'OPTIMIZED_FOR_OFFER_MIX',
      purchasePlanId: purchasePlanId,
      optimizedForOfferId: optimizedForOfferId,
      selectedTierId: selectedTerm.tierId,
      selectedTerm: {
        duration: selectedTerm.duration,
        pricingType: selectedTerm.pricingType,
      },
      usePrimaryCrossReferences,
      useLockedAllocations,
    });
  }

  /**
   * We'll get a 204 No Content response if this succeeds
   *
   * @deprecated We (Kemi and John) believe this function is no longer used and should not be called.
   */
  loadGroupPlan(rfpId: number, usePrimaryCrossReferences?: boolean) {
    console.log('TODO: api.rfps.loadGroupPlan has been deprecated.');

    return this.$http.put(`${this.baseUrl}/${rfpId}/purchaseplan`, {
      source: 'GROUP_PLAN',
      usePrimaryCrossReferences,
    });
  }

  getGenericPlanName(rfpId: number): ng.IPromise<any> {
    // TODO: Needs to be in a proper JSEND reponse
    return this.$http.get(`${this.baseUrl}/${rfpId}/genericplanname`);
  }

  savePlan(rfpId: number, newPlanName: string): ng.IPromise<any> {
    return this.$http.post(`${this.baseUrl}/${rfpId}/purchaseplans`, {
      purchasePlanName: newPlanName,
    });
  }

  loadOffers(rfpId: number, sellerId: number): ng.IPromise<Offer[]> {
    return this.$http.get(
      `${this.baseUrl}/${rfpId}/sellers/${sellerId}/offers`,
    );
  }

  createOffer(rfpId: number, sellerId: number): ng.IPromise<any> {
    return this.$http.post(
      `${this.baseUrl}/${rfpId}/sellers/${sellerId}/offers`,
      null,
    );
  }

  createOfferWithTiers(
    rfpId: number,
    sellerId: number,
    tiers: any[],
    spendLevelType: any,
    commitmentTypes?: Array<string>,
  ): ng.IPromise<any> {
    return this.$http.post(
      `${this.baseUrl}/${rfpId}/sellers/${sellerId}/offers`,
      {
        commitmentTypes,
        tiers,
        spendLevelTypeName: spendLevelType.name,
      },
    );
  }

  createOfferFromPricingModel(
    rfpId: number,
    sellerId: number,
    pricingModelOptions: PricingModelOptions,
  ): ng.IPromise<any> {
    return this.$http.post(
      `${this.baseUrl}/${rfpId}/sellers/${sellerId}/offers`,
      pricingModelOptions,
    );
  }

  getPricingModels(rfpId: number, sellerId: number): ng.IPromise<any> {
    return this.$http.get(
      `${this.baseUrl}/${rfpId}/sellers/${sellerId}/pricingmodels`,
    );
  }

  // Gets attached upload files - in use @ requestForPricing page
  getAttachments(rfpId: number): ng.IPromise<any> {
    return this.$http.get(`${this.baseUrl}/${rfpId}/documents`);
  }

  // Uploads selected attachments - in use @ requestForPricing page
  // @suppressAction: falsey - prevent interceptor from handling HTTP error - in multi-file upload components generally pass true
  uploadAttachments(
    rfpId: number,
    Upload,
    files: any[],
    suppressAction,
  ): ng.IPromise<any> {
    return Upload.upload({
      url: `${this.baseUrl}/${rfpId}/documents`,
      arrayKey: '',
      data: {
        files,
      },
      suppressAction,
    });
  }

  // Delete Uploaded attachments - in use @ requestForPricing page
  // @suppressAction: falsey - prevent interceptor from handling HTTP error - in multi-file upload components generally pass true
  deleteAttachments(
    rfpId: number,
    fileId: string,
    suppressAction,
  ): ng.IPromise<any> {
    return this.$http.delete(`${this.baseUrl}/${rfpId}/documents/${fileId}`, {
      suppressAction,
    });
  }
}
